'use client';

/**
 * The "manage columns" menu for a DataTable.
 *
 * A Radix dropdown of checkbox items, one per column the user may hide
 * (locked columns are never offered). It takes the table's columns and
 * visibility as props, so any page can mount it; pair it with
 * useColumnVisibility.
 *
 * Ported from glp-frontend `components/helpers/data-table/column-manager.tsx`.
 */
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { Check, Columns3 } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import './column-meta';
import { columnKey, columnLabel, manageableColumns } from './columnVisibility';

export interface ColumnManagerProps<TData> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  columnVisibility: VisibilityState;
  onToggle: (key: string) => void;
  onShowAll: () => void;
  className?: string;
}

export function ColumnManager<TData>({
  columns,
  columnVisibility,
  onToggle,
  onShowAll,
  className,
}: ColumnManagerProps<TData>) {
  const manageable = manageableColumns(columns);
  const hiddenCount = manageable.filter((c) => columnVisibility[columnKey(c)] === false).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type='button'
        aria-label='Manage columns'
        title='Manage columns'
        className={cn(
          'relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[19px] border-2 border-[rgba(193,199,214,0.35)] bg-white text-gray-600 hover:border-global-green hover:text-global-green',
          className,
        )}
      >
        <Columns3 className='h-5 w-5' strokeWidth={1.5} />
        {hiddenCount > 0 && (
          <span className='absolute -right-1 -top-1 rounded-full bg-global-green px-1.5 text-xs font-semibold leading-4 text-accessible-text-color'>
            {hiddenCount}
          </span>
        )}
      </DropdownMenuTrigger>
      {/* Capped to the space Radix reports and scrollable, so a long column
          list stays reachable on a phone (glp's menu overflows the screen). */}
      <DropdownMenuContent
        align='end'
        className='max-h-[var(--radix-dropdown-menu-content-available-height)] w-56 overflow-y-auto bg-white'
      >
        <DropdownMenuLabel>Manage columns</DropdownMenuLabel>
        <DropdownMenuSeparator className='bg-gray-100' />
        {/* onSelect preventDefault keeps the menu open, so several columns
            can be toggled in one visit. */}
        <DropdownMenuItem
          className='cursor-pointer px-2 focus:bg-gray-100'
          onSelect={(event) => {
            event.preventDefault();
            onShowAll();
          }}
        >
          Show all
        </DropdownMenuItem>
        <DropdownMenuSeparator className='bg-gray-100' />
        {manageable.map((column) => {
          const key = columnKey(column);
          return (
            <DropdownMenuPrimitive.CheckboxItem
              key={key}
              className='group relative flex cursor-pointer select-none items-center gap-3 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
              checked={columnVisibility[key] !== false}
              onCheckedChange={() => onToggle(key)}
              onSelect={(event) => event.preventDefault()}
            >
              {/* The system checkbox look: a rounded box that fills green when
                  checked. */}
              <span className='flex h-4 w-4 shrink-0 items-center justify-center rounded-md border-2 border-gray-300 bg-white transition-colors group-hover:border-gray-400 group-data-[state=checked]:border-global-green group-data-[state=checked]:bg-global-green'>
                <DropdownMenuPrimitive.ItemIndicator>
                  <Check className='h-2.5 w-2.5 text-white' strokeWidth={4} />
                </DropdownMenuPrimitive.ItemIndicator>
              </span>
              {columnLabel(column)}
            </DropdownMenuPrimitive.CheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ColumnManager;
